import { PointGps } from './pointGps';
import { ArchiveProvider } from '@educraft/base';

/**
 * Corner coordinate data from MAP file
 */
interface Corner {
  x: number;
  y: number;
  lat: number;
  lon: number;
}

/**
 * MercatorMap - handles Mercator projection map data and coordinate conversions
 */
export class MercatorMap {
  private constructor(
    public readonly imageBytes: Uint8Array,
    public readonly imageType: 'png' | 'jpeg',
    public readonly width: number,
    public readonly height: number,
    private readonly lonLeft: number,
    private readonly lonRight: number,
    private readonly mercYTop: number,
    private readonly mercYBottom: number
  ) {}

  /**
   * Load map from pre-extracted data (image bytes and map metadata)
   */
  public static fromData(
    imageBytes: Uint8Array,
    imageType: 'png' | 'jpeg',
    width: number,
    height: number,
    corners: { tl: Corner; tr: Corner; br: Corner; bl: Corner }
  ): MercatorMap {
    // Average longitude for left/right edges
    const lonLeft = (corners.tl.lon + corners.bl.lon) * 0.5;
    const lonRight = (corners.tr.lon + corners.br.lon) * 0.5;

    // Mercator Y for top/bottom edges
    const mercYTop =
      (MercatorMap.latToMercY(corners.tl.lat) + MercatorMap.latToMercY(corners.tr.lat)) * 0.5;
    const mercYBottom =
      (MercatorMap.latToMercY(corners.bl.lat) + MercatorMap.latToMercY(corners.br.lat)) * 0.5;

    return new MercatorMap(
      imageBytes,
      imageType,
      width,
      height,
      lonLeft,
      lonRight,
      mercYTop,
      mercYBottom
    );
  }

  /**
   * Load MercatorMap from archive using ArchiveProvider
   * @param archive ArchiveProvider instance
   * @param pack Archive pack name (e.g., 'map/map-cs.zip')
   */
  public static loadFromArchive(archive: ArchiveProvider, pack: string): MercatorMap {
    const files = archive.getFileList(pack);

    const mapFile = files.find((f: string) => f.toLowerCase().endsWith('.map'));
    if (!mapFile) throw new Error(`No .map file found in ${pack}`);

    const imageFile = files.find((f: string) => {
      const lower = f.toLowerCase();
      return lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg');
    });
    if (!imageFile) throw new Error(`No image file found in ${pack}`);

    const mapText = archive.getText(pack, mapFile);
    const { tl, tr, br, bl, width, height } = MercatorMap.parseMapFile(mapText);

    const imageBytes = archive.getBytes(pack, imageFile);

    const imageType: 'png' | 'jpeg' = imageFile.toLowerCase().endsWith('.png') ? 'png' : 'jpeg';

    return MercatorMap.fromData(imageBytes, imageType, width, height, { tl, tr, br, bl });
  }

  /**
   * Parse OziExplorer MAP file format to extract corners
   */
  public static parseMapFile(mapText: string): {
    tl: Corner;
    tr: Corner;
    br: Corner;
    bl: Corner;
    width: number;
    height: number;
  } {
    const lines = mapText.split('\n').map((l) => l.trim());

    const parsePoint = (id: string): Corner => {
      const line = lines.find((l) => l.toLowerCase().startsWith(`${id.toLowerCase()  },`));
      if (!line) throw new Error(`Missing ${id} in MAP file`);

      const parts = line.split(',').map((p) => p.trim());

      const x = parseInt(parts[2], 10);
      const y = parseInt(parts[3], 10);

      const latDeg = parseFloat(parts[6]);
      const latMin = parseFloat(parts[7]);
      const latHem = parts[8].toUpperCase()[0];

      const lonDeg = parseFloat(parts[9]);
      const lonMin = parseFloat(parts[10]);
      const lonHem = parts[11].toUpperCase()[0];

      let lat = latDeg + latMin / 60.0;
      if (latHem === 'S') lat = -lat;

      let lon = lonDeg + lonMin / 60.0;
      if (lonHem === 'W') lon = -lon;

      return { x, y, lat, lon };
    };

    const tl = parsePoint('Point01');
    const tr = parsePoint('Point02');
    const br = parsePoint('Point03');
    const bl = parsePoint('Point04');

    // Calculate dimensions from corners
    const width = Math.max(tl.x, tr.x, br.x, bl.x) + 1;
    const height = Math.max(tl.y, tr.y, br.y, bl.y) + 1;

    return { tl, tr, br, bl, width, height };
  }

  /**
   * Convert pixel coordinates to GPS (lat, lon)
   */
  public pixelToLatLon(x: number, y: number): PointGps {
    if (this.width <= 1 || this.height <= 1) {
      throw new Error('Invalid map dimensions');
    }

    const fx = x / (this.width - 1.0);
    const fy = y / (this.height - 1.0);

    // Longitude is linear in Mercator
    const lon = this.lonLeft + fx * (this.lonRight - this.lonLeft);

    // Mercator Y is linear along y axis (y increases downward in image)
    const mercY = this.mercYTop + fy * (this.mercYBottom - this.mercYTop);
    const lat = MercatorMap.mercYToLat(mercY);

    return new PointGps(lat, lon);
  }

  /**
   * Convert GPS (lat, lon) to pixel coordinates
   */
  public latLonToPixel(point: PointGps): { x: number; y: number } {
    if (this.width <= 1 || this.height <= 1) {
      throw new Error('Invalid map dimensions');
    }

    const fx = (point.lon - this.lonLeft) / (this.lonRight - this.lonLeft);
    const mercY = MercatorMap.latToMercY(point.lat);
    const fy = (mercY - this.mercYTop) / (this.mercYBottom - this.mercYTop);

    const x = fx * (this.width - 1.0);
    const y = fy * (this.height - 1.0);

    return { x, y };
  }

  /**
   * Convert latitude (degrees) to Mercator Y
   */
  private static latToMercY(latDeg: number): number {
    const maxLat = 85.05112878;
    const clampedLat = Math.max(-maxLat, Math.min(maxLat, latDeg));
    const d = (clampedLat * Math.PI) / 180.0;
    return Math.log(Math.tan(Math.PI / 4.0 + d / 2.0));
  }

  /**
   * Convert Mercator Y to latitude (degrees)
   */
  private static mercYToLat(y: number): number {
    const d = 2.0 * Math.atan(Math.exp(y)) - Math.PI / 2.0;
    return (d * 180.0) / Math.PI;
  }
}
